import { pick, prop } from 'ramda';
import { noop } from 'js/utils/methods.util';
import { useTranslations } from 'js/hooks/i18n';
import { Form } from 'js/components/common/Form';
import { useComponentConfig } from 'js/hooks/component';
import React, { memo, useCallback, useMemo } from 'react';
import { MultiSelect, Select } from 'js/components/common/FormFields';
import { AccessibilityElement } from 'js/components/common/AccessibilityElement';

import * as constants from '../../constants';
import { useGetFilterOptions } from '../../hooks';
import * as actions from '../../../../store/actions';
import { IGamesGridConfig, TFilterSortBy } from '../../../../types';
import { useDynamicReducerDispatch, useDynamicReducerSelector } from '../../../../store';

import style from './DesktopGamesFilters.scss';

export const DesktopGamesFiltersBare: React.FC = () => {
    const {
        isSortingEnabled,
        isLabelFilterEnabled,
        isProviderFilterEnabled,
        isCategoryFilterEnabled,
        isJackpotSortingEnabled
    } = useComponentConfig<IGamesGridConfig>();
    const translate = useTranslations();
    const dispatch = useDynamicReducerDispatch();
    const isFiltersChanged = useDynamicReducerSelector(prop('isFiltersChanged'));
    const filtersState = useDynamicReducerSelector(pick(['filterSortBy', 'filterProviders', 'filterLabels', 'filterCategory', 'filterCustomFields']));

    const filters = useGetFilterOptions();

    const formInitialValues = useMemo(() => ({
        'games-grid-filters-sort-by': filtersState.filterSortBy,
        'games-grid-filters-game-provider': filtersState.filterProviders,
        'games-grid-filters-game-label': filtersState.filterLabels,
        'games-grid-filters-game-category': filtersState.filterCategory,
        ...Object.keys(filtersState.filterCustomFields).reduce((acc, key) => {
            acc[`games-grid-filters-${key}`] = filtersState.filterCustomFields[key];

            return acc;
        }, {} as {[key: string]: string })
    }), [filtersState]);

    const handleSortByChange = useCallback((event: React.FocusEvent<HTMLSelectElement>) => {
        dispatch(actions.updateFilterSortBy(event.target.value as TFilterSortBy));
    }, [dispatch]);

    const handleProviderChange = useCallback((event: { target: { value: string } }) => {
        dispatch(actions.updateFilterProviders(event.target.value));
    }, [dispatch]);

    const handleLabelsChange = useCallback((event: { target: { value: string } }) => {
        dispatch(actions.updateFilterLabels(event.target.value));
    }, [dispatch]);

    const handleCategoryChange = useCallback((event: { target: { value: string } }) => {
        dispatch(actions.updateFilterCategory(event.target.value));
    }, [dispatch]);

    const handleCustomFieldChange = useCallback((fieldName) => (event: { target: { value: string } }) => {
        dispatch(actions.updateFilterCustomField({ fieldName, value: event.target.value }));
    }, [dispatch]);

    const handleClearFiltersClick = useCallback(() => {
        dispatch(actions.resetFilters());
    }, [dispatch]);

    const sortingOptions = isJackpotSortingEnabled ? constants.SORT_BY_OPTIONS_WITH_JACKPOTS : constants.SORT_BY_OPTIONS;

    return (
        <Form
            onSubmit={noop}
            className="cms-games-grid-desktop-filters"
            initialValues={formInitialValues}
            render={() => (
                <>
                    {isProviderFilterEnabled && Boolean(filters.providerOptions.length) && (
                        <MultiSelect
                            className={style.field}
                            id="games-grid-filters-game-provider"
                            name="games-grid-filters-game-provider"
                            aria-label={translate('playtech.mobile.system.games-grid.filters.provider.label')}
                            placeholder={translate('playtech.mobile.system.games-grid.filters.provider.label')}
                            onChange={handleProviderChange}
                            classNames={{
                                innerContainer: style.innerContainer,
                                displayValue: style.displayValue
                            }}
                            options={filters.providerOptions}
                        />
                    )}
                    {isCategoryFilterEnabled && Boolean(filters.categoryOptions.length) && (
                        <MultiSelect
                            className={style.field}
                            id="games-grid-filters-game-category"
                            name="games-grid-filters-game-category"
                            aria-label={translate('playtech.mobile.system.games-grid.filters.category.label')}
                            placeholder={translate('playtech.mobile.system.games-grid.filters.category.label')}
                            onChange={handleCategoryChange}
                            classNames={{
                                innerContainer: style.innerContainer,
                                displayValue: style.displayValue
                            }}
                            options={filters.categoryOptions}
                        />
                    )}
                    {isLabelFilterEnabled && Boolean(filters.labelOptions.length) && (
                        <MultiSelect
                            className={style.field}
                            id="games-grid-filters-game-label"
                            name="games-grid-filters-game-label"
                            aria-label={translate('playtech.mobile.system.games-grid.filters.label.label')}
                            placeholder={translate('playtech.mobile.system.games-grid.filters.label.label')}
                            onChange={handleLabelsChange}
                            classNames={{
                                innerContainer: style.innerContainer,
                                displayValue: style.displayValue
                            }}
                            options={filters.labelOptions}
                        />
                    )}
                    {Boolean(filters?.customFieldsOptions?.length) && filters.customFieldsOptions.map((customField) => (
                        <MultiSelect
                            className={style.field}
                            key={customField.name}
                            id={`games-grid-filters-${customField.name}`}
                            name={`games-grid-filters-${customField.name}`}
                            aria-label={translate(`playtech.mobile.system.games-grid.filters.customField.${customField.name}.label`, customField.name)}
                            placeholder={translate(`playtech.mobile.system.games-grid.filters.customField.${customField.name}.label`, customField.name)}
                            onChange={handleCustomFieldChange(customField.name)}
                            classNames={{
                                innerContainer: style.innerContainer,
                                displayValue: style.displayValue
                            }}
                            options={customField.options}
                        />
                    ))}
                    {isSortingEnabled && (
                        <div className={style.sortByContainer}>
                            <span className={style.selectLabel}>{translate('playtech.mobile.system.games-grid.filters.sortBy.label')}</span>
                            <Select
                                className={style.field}
                                id="games-grid-filters-sort-by"
                                name="games-grid-filters-sort-by"
                                aria-label={translate('playtech.mobile.system.games-grid.filters.sortBy.label')}
                                onChange={handleSortByChange}
                                classNames={{
                                    innerContainer: style.innerContainer,
                                    select: style.select,
                                    selectDummy: style.displayValue
                                }}
                                options={sortingOptions.map(({ key, value, label }) => ({
                                    key,
                                    value,
                                    label: translate(label)
                                }))}
                            />
                        </div>
                    )}
                    {isFiltersChanged && (
                        <AccessibilityElement onClick={handleClearFiltersClick}>
                            <div className={style.clearButton}>{translate('playtech.mobile.system.games-grid.filters.clearFilters')}</div>
                        </AccessibilityElement>
                    )}
                </>
            )}
        >
        </Form>
    );
};

export const DesktopGamesFilters = memo(DesktopGamesFiltersBare);
